import { createPortal } from 'react-dom'
import type { CSSProperties, ReactNode } from 'react'
import { overlayRoot, useOverlay } from '../../lib/overlay'

/**
 * The overlay shell — three sizes, one anatomy. Every sheet, wizard, record
 * document and sub-flow in the product is one of these; none of them
 * re-implements the header, the close button or the footer.
 *
 *   form   a 520px column hanging from the top of the frame — the sub-flows
 *   wide   760–780px, for wizards and the plan chooser
 *   record a near-full-bleed document — a client, a person, an invoice, a
 *          project. It is the record itself, not a form about it, which is why
 *          it takes almost the whole frame and a larger radius.
 *
 * Geometry is the prototype's, plus ROOM below: each sheet hangs from a
 * slightly different top (60–84px) and reserves a slightly different amount of
 * bottom room, and those numbers are per-sheet rather than per-size, so `top`
 * and `maxHeight` are props with the common answer as the default.
 */

export type SheetVariant = 'form' | 'wide' | 'record'

const SCRIM_Z: Record<SheetVariant, number> = { form: 36, wide: 36, record: 26 }
const PANEL_Z: Record<SheetVariant, number> = { form: 37, wide: 37, record: 27 }

const DEFAULT_WIDTH: Record<SheetVariant, number> = { form: 520, wide: 760, record: 0 }

/**
 * How much more of the frame every overlay leaves showing than the prototype
 * drew (Alex, 2026-09-01: "being so big is claustrophobic").
 *
 * Added here rather than by editing each sheet's own numbers, because those
 * numbers carry a relationship worth keeping — a receipt hangs higher than the
 * billing sheet, a wizard reserves less below than a payment form — and
 * rewriting eight of them by hand is how that relationship gets lost one sheet
 * at a time. One constant moves them all and keeps the distances between them.
 *
 * Only the desktop geometry: the phone and tablet rules in application.css
 * (`.sheet`, `.panel`) override inset, top and bottom with `!important`, and a
 * record document is still the whole screen at 390px.
 */
const ROOM = 24

interface SheetProps {
  open: boolean
  onClose: () => void
  variant?: SheetVariant
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  /** Actions, right-aligned after the note. Primary last. */
  footer?: ReactNode
  /** The 500 11px muted line that takes the footer's spare width. */
  footerNote?: ReactNode
  /** Overrides DEFAULT_WIDTH. Ignored by the record variant. */
  width?: number
  /** Distance from the top of the frame. Ignored by the record variant. */
  top?: number
  /** The room left below, as the `calc(100% - n)` subtrahend. */
  bottomRoom?: number
  /**
   * `stack` gives the body the standard 18px padding and 14px column gap.
   * `bare` leaves it empty, for record documents that lay out their own grid.
   */
  body?: 'stack' | 'bare'
  bodyStyle?: CSSProperties
  bodyClassName?: string
  /** Extra controls in the header, between the title and the close button. */
  headerActions?: ReactNode
  labelledBy?: string
  /** Shows amounts inside this sheet even while the curtain is drawn — see
   *  lib/financesCovered.ts. On the panel rather than on the body, so the
   *  footer's own figures ("Raise £1,200") are covered by the same answer. */
  moneyOpen?: boolean
}

export function Sheet({
  open,
  onClose,
  variant = 'form',
  title,
  subtitle,
  children,
  footer,
  footerNote,
  width,
  top = 78,
  bottomRoom = 120,
  body = 'stack',
  bodyStyle,
  bodyClassName = '',
  headerActions,
  labelledBy,
  moneyOpen = false,
}: SheetProps) {
  const panel = useOverlay(open, onClose)
  const root = overlayRoot()

  if (!open || !root) return null

  const isRecord = variant === 'record'
  const panelStyle: CSSProperties = isRecord
    ? { position: 'absolute', inset: 34 + ROOM, borderRadius: 18 }
    : {
        position: 'absolute',
        top: top + ROOM,
        left: '50%',
        transform: 'translateX(-50%)',
        width: `min(${width ?? DEFAULT_WIDTH[variant]}px,calc(100% - ${40 + ROOM * 2}px))`,
        // Twice, because `top` has already taken one helping off the top and
        // the room below should match it rather than be half of it.
        maxHeight: `calc(100% - ${bottomRoom + ROOM * 2}px)`,
        borderRadius: 16,
      }

  return createPortal(
    <>
      {/* One z-index below the panel, and clicking it closes. A div rather than
          a button: it is 100% of the frame, and a button that size is announced
          as one control covering the whole screen. Escape is the keyboard
          route, and it is handled in lib/overlay.ts. */}
      <div
        className="tab"
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--w-scrim)',
          zIndex: SCRIM_Z[variant],
          cursor: 'default',
        }}
      />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        data-money-open={moneyOpen ? '' : undefined}
        className={`fr ${isRecord ? 'sheet' : 'panel'}`}
        style={{
          ...panelStyle,
          border: '1px solid var(--w-line-strong)',
          background: 'var(--w-sheet)',
          boxShadow: 'var(--w-float)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
          zIndex: PANEL_Z[variant],
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 18px',
            borderBottom: '1px solid var(--w-line)',
            flex: 'none',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              id={labelledBy}
              style={{
                font: '600 14px Manrope,sans-serif',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </div>
            {subtitle && (
              <div
                style={{
                  font: '400 11px Manrope,sans-serif',
                  color: 'var(--color-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {headerActions}
          <button
            type="button"
            className="tab"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              border: 0,
              background: 'var(--w-fill)',
              font: '400 16px/24px Manrope,sans-serif',
              color: 'var(--color-muted)',
              textAlign: 'center',
              cursor: 'pointer',
              flex: 'none',
            }}
          >
            ×
          </button>
        </div>

        <div
          className={`panel-body ${bodyClassName}`}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            ...(body === 'stack'
              ? { padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }
              : null),
            ...bodyStyle,
          }}
        >
          {children}
        </div>

        {(footer || footerNote) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '13px 18px',
              borderTop: '1px solid var(--w-line)',
              background: 'var(--w-sunk)',
              flex: 'none',
              flexWrap: 'wrap',
            }}
          >
            {footerNote && (
              <span
                style={{
                  font: '500 11px/1.5 Manrope,sans-serif',
                  color: 'var(--color-muted)',
                  flex: 1,
                  minWidth: 140,
                }}
              >
                {footerNote}
              </span>
            )}
            {!footerNote && <span style={{ flex: 1 }} />}
            {footer}
          </div>
        )}
      </div>
    </>,
    root
  )
}
